import React, { createContext, useContext, useState, useEffect } from "react";
import type { ReactNode } from "react";

// Available locales
export type Locale = "fi" | "en";

// Language context type
interface LanguageContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: string) => string;
}

// Translations object
const translations: Record<Locale, Record<string, string>> = {
  fi: {
    "lang.switch": "English",
    "lang.current": "Suomi",
    loading: "Ladataan...",
    error: "Virhe",
    "post.backToList": "Takaisin",
    "post.readMore": "Lue lisää",
    "posts.noArticles": "Ei artikkeleita saatavilla",
    "posts.noContent": "Sisältöä julkaistaan tänne kun sitä on saatavilla.",
    "posts.article": "Artikkeli",
    "footer.title": "Pohjoismainen luonnonsuojelualoite",
    "footer.subtitle": "Taide • Tiede • Luonto",
    "footer.copyright": "© 2025 Pohjoismainen luonnonsuojelualoite",
    "footer.follow": "Seuraa",
    "loading.application": "Ladataan sovellusta...",
    "loading.article": "Ladataan artikkelia...",
    "loading.content": "Ladataan sisältöä...",
    "error.pageNotFound": "Sivua ei löytynyt",
    "landing.subtitle":
      "Suunnitelma uudesta kansallispuistosta, joka sijoittuisi Hyrynsalmen ja Puolangan kuntien alueelle Ylä-Kainuuseen.",
    "landing.title1": "Vaara-Kainuun",
    "landing.title2": "TAIDEKANSALLISPUISTO",
  },
  en: {
    "lang.switch": "Suomi",
    "lang.current": "English",
    loading: "Loading...",
    error: "Error",
    "post.backToList": "Back",
    "post.readMore": "Read more",
    "posts.noArticles": "No articles available",
    "posts.noContent": "Content will be published here when available.",
    "posts.article": "Article",
    "footer.title": "Nordic Nature Conservation Initiative",
    "footer.subtitle": "Arts • Science • Nature",
    "footer.copyright": "© 2025 Nordic Nature Conservation Initiative",
    "footer.follow": "Follow",
    "loading.application": "Loading application...",
    "loading.article": "Loading article...",
    "loading.content": "Loading content...",
    "error.pageNotFound": "Page not found",
    "landing.subtitle":
      "A proposal for a new national park that would be located in the municipalities of Hyrynsalmi and Puolanka in Upper Kainuu.",
    "landing.title1": "Vaara-Kainuu",
    "landing.title2": "ART NATIONAL PARK",
  },
};

// Create context
const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined
);

// Language provider component
interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({
  children,
}) => {
  // Initialize from localStorage or default to Finnish
  const [locale, setLocaleState] = useState<Locale>(() => {
    const saved = localStorage.getItem("locale");
    return saved === "en" || saved === "fi" ? saved : "fi";
  });

  // Translation function
  const t = (key: string): string => {
    return translations[locale][key] || key;
  };

  // Update locale and save to localStorage
  const setLocale = (newLocale: Locale) => {
    setLocaleState(newLocale);
    localStorage.setItem("locale", newLocale);
  };

  // Update document lang attribute
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const value: LanguageContextType = {
    locale,
    setLocale,
    t,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

// Hook to use language context
export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
